import { generateDraft, generateSpeakerPipelineDraft, generateEventDraft } from './templates.js';

/** Build a mailto: URI, handling the space-encoding quirk mailto needs. */
function buildMailto(to, subject, body) {
  const params = new URLSearchParams();
  params.set('subject', subject);
  params.set('body', body);
  // URLSearchParams encodes space as +, mailto needs %20
  const qs = params.toString().replace(/\+/g, '%20');
  return `mailto:${encodeURIComponent(to)}?${qs}`;
}

/**
 * Build a mailto: URI from an engagement template and engagement context.
 * Opens in the user's default mail client with recipient, subject, body prefilled.
 *
 * @param {string} templateKey
 * @param {object} engagement
 * @param {object} event
 * @param {object} speaker
 * @param {{name?: string, email?: string, chapterName?: string}} [sender]  The signed-in user sending this email
 * @returns {string} mailto: URI
 */
export function buildMailtoLink(templateKey, engagement, event, speaker, sender) {
  const { subject, body } = generateDraft(templateKey, engagement, event, speaker, sender);
  return buildMailto(speaker?.email ?? '', subject, body);
}

/**
 * Build a mailto: URI from a pre-booking pipeline template. Recipient is
 * the speaker's own email.
 *
 * @param {string} templateKey  One of SPEAKER_COMM_TYPES
 * @param {object} speaker
 * @param {object[]} [upcomingEvents]
 * @param {{name?: string, email?: string, chapterName?: string}} [sender]  The signed-in user sending this email
 * @returns {string} mailto: URI
 */
export function buildSpeakerMailtoLink(templateKey, speaker, upcomingEvents = [], sender) {
  const { subject, body } = generateSpeakerPipelineDraft(templateKey, speaker, upcomingEvents, sender);
  return buildMailto(speaker?.email ?? '', subject, body);
}

/**
 * Build a mailto: URI from an event-level template, addressed to every
 * speaker passed in (comma-separated — mailto: supports multiple
 * recipients this way). Pass an empty array for "Call for speakers", since
 * there's no engaged speaker yet — the organizer fills in the recipient(s)
 * themselves in their mail client.
 *
 * @param {string} templateKey  One of EVENT_COMM_TYPES
 * @param {object} event
 * @param {object[]} [speakers]
 * @param {{name?: string, email?: string, chapterName?: string}} [sender]  The signed-in user sending this email
 * @returns {string} mailto: URI
 */
export function buildEventMailtoLink(templateKey, event, speakers = [], sender) {
  const { subject, body } = generateEventDraft(templateKey, event, speakers, sender);
  const to = speakers.map((s) => s.email).filter(Boolean).join(',');
  return buildMailto(to, subject, body);
}
